import 'regenerator-runtime/runtime'
import { Box, InputBase, Theme, Typography } from '@mui/material'
import UploadDropZone from '@rpldy/upload-drop-zone'
import { FileFilterMethod, useBatchAddListener, useUploady } from '@rpldy/uploady'
import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'
import { track, eventTypes } from 'torqcloud-ui'

import { ACCEPT_FILE_TEXT_TYPES } from './consts'
import PromptAttachmentButton from './PromptAttachmentButton'
import ChatflowFileChip, { type ChatflowFileChipProps } from './PromptBox.ChatflowFileChip'
import PromptBoxDeepResearchButton from './PromptBox.DeepResearchButton'
import PromptDropZoneOverlay from './PromptBox.DropZoneOverlay'
import PromptBoxThinkingOverlay from './PromptBox.ThinkingOverlay'
import type { PromptBoxWrapperProps } from './PromptBox.wrapper.tsx'

import './PromptBox.css'

const MAX_SIZE = 20000000 // 20MB in bytes
const FILE_ATTACHMENT_LIMIT = 9
export type PromptBoxForm = {
  prompt: string
  files: Pick<ChatflowFile, 'file' | 'id' | 'status'>[] // Only send necessary data
  enableDeepResearch?: boolean
  enableFirecrawl?: boolean
}

interface PromptBoxBase {
  placeholder?: string
  color?: 'grey' | 'dark-grey'
  variant?: 'small' | 'large'
  initialPrompt?: string
  onSubmit: (values: PromptBoxForm) => Promise<void>
  submitDisabled?: boolean
  inputDisabled?: boolean
  blockedReason?: string
  allowDeepResearch?: boolean
  allowWebSearch?: boolean
  onReady?: (ref: React.MutableRefObject<PromptBoxRef>) => Promise<void>
}

type PromptBoxPropsWithoutFileUpload = PromptBoxBase & {
  allowUpload?: false
}

type PromptBoxPropsWithDeferredFileUpload = PromptBoxBase & {
  allowedFileTypes?: PromptBoxWrapperProps['allowedFileTypes']
  allowUpload: true
  deferFileUpload: true
}

type PromptBoxPropsWithImmediateFileUpload = PromptBoxBase & {
  allowedFileTypes?: PromptBoxWrapperProps['allowedFileTypes']
  allowUpload: true
  deferFileUpload?: false
  onFilesAdded: (files: File[]) => Promise<void>
  onFileDeleted: (chatfileId: string) => Promise<boolean> // Return if PromptBox should remove file
  onFileRetry: (filename: string, file: File) => Promise<void>
}

export type PromptBoxProps = PromptBoxPropsWithDeferredFileUpload | PromptBoxPropsWithImmediateFileUpload | PromptBoxPropsWithoutFileUpload

type Filename = string
type ChatflowFile = ChatflowFileChipProps & { file?: File }

type FileStatusBaseArgs = { filename: string; tooltip?: React.ReactNode; disableRetry?: boolean }

type FileStatusArgs =
  | ({ status: 'pending' } & FileStatusBaseArgs)
  | ({ status: 'processing' } & FileStatusBaseArgs)
  | ({ status: 'failure' } & FileStatusBaseArgs)
  | ({ status: 'success'; id: string } & FileStatusBaseArgs)

export interface PromptBoxRef {
  /**
   * Replace the whole prompt with the contents passed to `setPrompt`.
   */
  setPrompt: (prompt: string) => void
  /**
   * Replace _only_ the instructions for the prompt. The only way to set
   * instructions is to pass it to this function.
   */
  setInstructions: (instructions?: string) => void
  /**
   * Updates a file chip to the proper status
   * @argument filename - Internal id only within PromptBox for tracking different files
   * @argument chatfileId - The id returned from the api
   */
  setFileStatus: (args: FileStatusArgs) => void
  /**
   * Get all files in the current internal state
   */
  getFiles: () => File[]
  /**
   * Add existing files to the promptbox - useful for when the user hasn't used
   * a file with a message.
   */
  appendFiles: (files: Exclude<ChatflowFileChipProps[], 'onDelete'>) => void
  /**
   * Get the internal file state
   */
  getFileMap: () => Record<Filename, ChatflowFile>
  /**
   * Remove files from the PromptBox by their filename
   */
  removeFiles: (filenames: string[]) => void
  /**
   * Remove files from the PromptBox
   */
  clearFiles: () => void
  /**
   * Show/hide the thinking modal
   */
  showThinkingModal: (isThinking: boolean) => void
  /**
   * Reset all files with 'success' status back to 'pending' status
   */
  resetAllFilesToPending: () => void
}

function PromptBoxComponent(
  {
    initialPrompt,
    placeholder,
    color = 'grey',
    variant = 'large',
    onSubmit,
    submitDisabled,
    inputDisabled,
    blockedReason,
    allowDeepResearch,
    allowUpload,
    allowWebSearch,
    onReady = async () => {},
    ...props
  }: PromptBoxProps,
  ref: React.ForwardedRef<PromptBoxRef>
) {
  const [isThinking, setIsThinking] = useState<boolean>(false)
  const allowedFileTypes = ('allowedFileTypes' in props && props.allowedFileTypes) || ACCEPT_FILE_TEXT_TYPES
  const deferFileUpload = 'deferFileUpload' in props && props.deferFileUpload === true

  const form = useForm<PromptBoxForm>({
    defaultValues: { prompt: initialPrompt ?? '', files: [] },
  })
  const prompt = form.watch('prompt')
  const [_, setInstructions_] = useState<string | undefined>()

  /* --- Speech Recognition ------------------------------------------------- */
  const { listening: isListening, finalTranscript, resetTranscript } = useSpeechRecognition()
  useEffect(() => {
    if (!finalTranscript) return

    form.setValue('prompt', `${prompt} ${finalTranscript}`)
    resetTranscript()

    /**
     * setValue        : Form values change, but the fn setValue doesnt change
     * resetTranscript : Fn doesn't need to change on reload
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalTranscript])

  /* --- File upload -------------------------------------------------------- */
  const uploady = useUploady()

  const [files, setFiles] = useState<Record<Filename, ChatflowFile>>({})
  const currentFileCount = Object.keys(files).length

  const fileFilter = useCallback<FileFilterMethod>(
    (file, index) => {
      const additionalFilesAllowed = Math.max(FILE_ATTACHMENT_LIMIT - currentFileCount, 0)
      if (additionalFilesAllowed === index) {
        toast.error(`You can only attach ${FILE_ATTACHMENT_LIMIT} file(s).`)
      }
      if (index >= additionalFilesAllowed) {
        return false
      }

      if (typeof file === 'string') return false // Should never happen, but if it does, reject plain strings; type guard

      if (!allowedFileTypes.includes(file.type)) {
        toast.error(`File ${file.name} has an unsupported type (${file.type})`)
        return false
      } else if (file.size > MAX_SIZE) {
        // File must be less than 20MB
        toast.error(
          `The selected file ${file.name} is too large (${Math.round((file.size / 1000000) * 10) / 10} MB). The max supported size is ${
            MAX_SIZE / 1000000
          } MB`
        )
        return false
      }

      return true
    },
    [allowedFileTypes, currentFileCount]
  )

  useEffect(() => {
    if (!uploady) return
    uploady.setOptions({ autoUpload: false, fileFilter })
  }, [fileFilter, uploady])

  const getFiles = useCallback(
    () =>
      Object.values(files)
        .map(f => f.file)
        .filter(f => !!f),
    [files]
  )

  const setFileStatus = useCallback((args: FileStatusArgs) => {
    setFiles(prev => ({
      ...prev,
      [args.filename]: {
        ...(prev[args.filename] || {}),
        id: args.status === 'success' ? args.id : undefined,
        status: args.status,
        tooltip: args.status === 'failure' ? args.tooltip : undefined,
        onRetry: args.disableRetry ? () => {} : prev[args.filename]?.onRetry ?? (() => {}),
      },
    }))
  }, [])

  const handleFileDelete = useCallback(
    async (filename: Filename) => {
      const shouldDelete =
        deferFileUpload || !allowUpload || (await (props as PromptBoxPropsWithImmediateFileUpload).onFileDeleted(filename))
      if (!shouldDelete) return

      setFiles(prev => {
        const files: Record<Filename, ChatflowFile> = {}

        for (const [key, file] of Object.entries(prev)) {
          if (key !== filename) {
            files[key] = file
          }
        }

        return files
      })
    },
    [allowUpload, deferFileUpload, props]
  )

  const handleFileRetry = useCallback(
    async (file: File) => {
      setFileStatus({ status: 'processing', filename: file.name })
      if (allowUpload && !deferFileUpload) {
        await (props as PromptBoxPropsWithImmediateFileUpload).onFileRetry(file.name, file)
      }
    },
    [allowUpload, deferFileUpload, props, setFileStatus]
  )

  useBatchAddListener(batch => {
    const files: File[] = []
    for (const item of batch.items) {
      const file = item.file as File
      setFiles(prev => ({
        ...prev,
        [file.name]: {
          name: file.name,
          status: deferFileUpload ? 'pending' : 'processing',
          file,
          onDelete: async () => await handleFileDelete(file.name),
          onRetry: async () => await handleFileRetry(file),
        },
      }))

      files.push(file)
    }

    if (allowUpload && !deferFileUpload) {
      void (props as PromptBoxPropsWithImmediateFileUpload).onFilesAdded(files)
    }
  })

  /* --- Handlers ----------------------------------------------------------- */
  const handleSubmit = useCallback(
    async (v: PromptBoxForm) => {
      // Extract only necessary properties on submit; functions can't be
      // serialized, which causes issues when passing messages via router/redux
      await onSubmit({
        ...v,
        files: Object.values(files).map(f => ({
          file: f.file,
          id: f.id,
          status: f.status,
        })),
      })
    },
    [files, onSubmit]
  )

  /**
   * Use the browser API to convert speech to text.
   * NOTE(myles): This doesn't seem to work in Brave or Firefox. Probably
   * should be a tech debt ticket.
   */
  const handleMicClick = useCallback(async () => {
    if (!isListening) {
      await SpeechRecognition.startListening({
        continuous: true,
        language: 'en-US',
      })
    } else {
      await SpeechRecognition.stopListening()
    }
  }, [isListening])

  const handleUploadFile = useCallback(() => {
    uploady.showFileUpload()
  }, [uploady])

  const handleKeydown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        // Respect the same disabled conditions as the submit button
        if (submitDisabled || !form.getValues('prompt').trim()) return
        await handleSubmit({
          ...form.getValues(),
          files: Object.values(files).map(f => ({
            file: f.file,
            id: f.id,
            status: f.status,
          })),
        })
      }
    },
    [files, form, handleSubmit, submitDisabled]
  )

  /* --- Forward ref methods ------------------------------------------------ */
  useImperativeHandle(
    ref,
    () => ({
      setPrompt: (prompt: string) => form.setValue('prompt', prompt),
      setInstructions: (instructions?: string) => {
        setInstructions_(prev => {
          let prompt = form.getValues('prompt')

          if (prev) {
            if (prompt.startsWith(prev)) {
              prompt = prompt.replace(prev, instructions ?? '')
            }
          } else if (instructions) {
            prompt = `${instructions} ${prompt}`
          }

          form.setValue('prompt', prompt.trim())
          return instructions
        })
      },
      setFileStatus,
      appendFiles: (files: Exclude<ChatflowFileChipProps[], 'onDelete'>) => {
        setFiles(prev => {
          const result = { ...prev }
          for (const file of files) {
            const name = file.name
            if (!name) continue // The file should have a name

            result[name] = {
              ...file,
              onDelete: async () => await handleFileDelete(name),
            }
          }

          return result
        })
      },
      getFiles,
      getFileMap: () => files,
      removeFiles: (filenames: string[]) => {
        setFiles(prev => {
          const result: Record<string, ChatflowFile> = {}
          for (const [key, file] of Object.entries(prev)) {
            if (!filenames.includes(key)) {
              result[key] = file
            }
          }
          return result
        })
      },
      clearFiles: () => setFiles({}),
      resetAllFilesToPending: () => {
        setFiles(prev => {
          const updated: Record<string, ChatflowFile> = {}
          for (const [key, file] of Object.entries(prev)) {
            updated[key] = {
              ...file,
              status: file.status === 'success' ? 'pending' : file.status,
              id: file.status === 'success' ? undefined : file.id,
            }
          }
          return updated
        })
      },
      showThinkingModal: b => setIsThinking(b),
    }),
    [setFileStatus, getFiles, form, files, handleFileDelete]
  )

  // Must be run right before render
  useEffect(() => {
    void onReady(ref as React.MutableRefObject<PromptBoxRef>)
    // We only want this to run once, on ready
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [isMultiline, setIsMultiline] = useState<boolean>(variant === 'large')
  const onInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (variant === 'large') return

      const target = e.target as HTMLTextAreaElement
      if (!target) return

      if (!target.value) {
        setIsMultiline(false)
        return
      }

      const computedStyle = window.getComputedStyle(target)
      const singleLineHeight = Number.parseFloat(computedStyle.lineHeight)
      const height = Number.parseFloat(computedStyle.height)

      if (height > singleLineHeight) {
        setIsMultiline(true)
      }
    },
    [variant]
  )

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '8px' }}>
      <FormProvider {...form}>
        {isThinking && <PromptBoxThinkingOverlay color={color} />}
        <UploadDropZone id="drop-zone" onDragOverClassName={allowUpload ? 'drop-zone-active' : undefined}>
          <Box
            component="form"
            id="prompt-box"
            sx={{
              background: t => (color === 'grey' ? t.colors.base[50] : t.colors.base[60]),
              padding: '16px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
            }}
            onClick={e => {
              if (inputDisabled) return
              if (!(e.target instanceof HTMLDivElement)) return
              const el = document.querySelector('#prompt-box__input') as HTMLTextAreaElement
              el.focus()
            }}
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <InputBase
              disabled={inputDisabled}
              readOnly={inputDisabled}
              sx={{
                mr: 1,
                display: 'inline-flex',
                order: isMultiline ? 1 : 3,
                flex: '1 0 auto',
              }}
              placeholder={inputDisabled ? undefined : placeholder}
              inputProps={{
                id: 'prompt-box__input',
                sx: {
                  width: 'calc(100% - 4px)',
                  pr: '4px',
                  scrollbarColor: (t: Theme) => `${t.colors.base[45]} transparent`,
                  font: (t: Theme) => ({
                    fontSize: t.typography.body1.fontSize,
                  }),
                  fontSize: '12px',
                  lineHeight: '1.6', // 25.6px ~ 26px
                },
              }}
              multiline
              minRows={variant === 'small' ? 1 : 2}
              maxRows={5}
              title="press [Enter] to submit, or [Shift]+[Enter] to insert a newline"
              aria-label="Message (press [Enter] to submit, or [Shift]+[Enter] to insert a newline)"
              onKeyDown={handleKeydown}
              {...form.register('prompt', { onChange: onInputChange })}
            />
            {(isMultiline || Object.keys(files).length > 0) && (
              <Box
                id="file-inputs"
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: '4px',
                  flexWrap: 'wrap',
                  order: isMultiline ? 2 : 1,
                  flex: '1 1 100%',
                }}
              >
                {Object.entries(files).map(([id, { file: _, ...rest }]) => (
                  <ChatflowFileChip key={id} {...rest} variant={color} />
                ))}
              </Box>
            )}
            <Box
              id="prompt-box__controls-left"
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'flex-start',
                gap: '8px',
                order: isMultiline ? 3 : 2,
                flex: isMultiline ? '1 0 auto' : undefined,
              }}
            >
              {allowDeepResearch && <PromptBoxDeepResearchButton variant={variant} />}
              {blockedReason && (
                <Typography
                  variant="body1"
                  sx={{ fontSize: '12px', color: t => t.colors.base[45], fontStyle: 'italic', alignSelf: 'center' }}
                >
                  {blockedReason}
                </Typography>
              )}
            </Box>
            <Box
              id="prompt-box__controls-right"
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
                gap: '16px',
                order: 4,
              }}
            >
              <Box id="prompt-box__controls" sx={{ display: 'flex', gap: '8px' }}>
                <Controller
                  name="enableFirecrawl"
                  control={form.control}
                  render={({ field }) => {
                    return (
                      <label htmlFor={field.name}>
                        <PromptAttachmentButton
                          icon="language"
                          hidden={!allowWebSearch}
                          active={field.value}
                          disabled={!!blockedReason}
                          // MUI buttons seem to act differently within labels; need to manually set the value within handler
                          onClick={_e => {
                            const nextValue = !field.value
                            track(eventTypes.WEB_SEARCH_TOGGLED, { enabled: nextValue })
                            form.setValue(field.name, nextValue)
                          }}
                        />
                        <input id={field.name} type="checkbox" style={{ display: 'none' }} {...form.register('enableFirecrawl')} />
                      </label>
                    )
                  }}
                />
                <PromptAttachmentButton
                  icon="attach"
                  hidden={!allowUpload}
                  onClick={handleUploadFile}
                  disabled={!!blockedReason || currentFileCount >= FILE_ATTACHMENT_LIMIT}
                />
                <PromptAttachmentButton
                  icon="mic"
                  variant={isListening ? 'pulse' : 'secondary'}
                  disabled={!!blockedReason}
                  onClick={handleMicClick}
                />
              </Box>
              <PromptAttachmentButton icon="send" variant="primary" type="submit" disabled={submitDisabled || prompt.length === 0} />
            </Box>
          </Box>
          <Box ref={ref} id="file-inputs" />
          <PromptDropZoneOverlay isMultiline={isMultiline} />
        </UploadDropZone>
      </FormProvider>
    </Box>
  )
}

export default React.forwardRef<PromptBoxRef, PromptBoxProps>(PromptBoxComponent)
